// "Feedback lessons" tab of the Agent Eval page: the learning-signal ledger
// (GET /lessons) with status / source / profile filters (server side) and a
// KB-write filter (client side over the loaded page), opening into
// LessonDetailView (GET /lessons/{id}).

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  LEARNING_SIGNAL_SOURCES,
  LEARNING_SIGNAL_STATUSES,
  SOURCE_DISPLAY,
  feedbackLearningApi,
  type LearningSignalSource,
  type LearningSignalStatus,
  type LedgerDetail,
  type LedgerSummary,
} from '../../services/feedbackLearning';
import styles from '../AgentEvalPage.module.css';
import { LessonDetailView } from './LessonDetailView';
import { errorMessage, formatTs, formatUsd } from './format';
import { matchesKbWriteFilter, type KbWriteFilter } from './lessonFilters';

const PAGE_LIMIT = 200;

function statusTagClass(status: LearningSignalStatus): string {
  switch (status) {
    case 'written':
      return `${styles.tag} ${styles.ok}`;
    case 'failed':
      return `${styles.tag} ${styles.flagged}`;
    case 'skipped':
    case 'superseded':
      return `${styles.tag} ${styles.label_other}`;
    default:
      return `${styles.tag} ${styles.pending}`;
  }
}

export function FeedbackLessonsTab(): React.ReactElement {
  const [statusFilter, setStatusFilter] = useState<'' | LearningSignalStatus>('');
  const [sourceFilter, setSourceFilter] = useState<'' | LearningSignalSource>('');
  const [profileFilter, setProfileFilter] = useState<string>('');
  const [kbWriteFilter, setKbWriteFilter] = useState<KbWriteFilter>('any');
  const [rows, setRows] = useState<LedgerSummary[]>([]);
  const [selected, setSelected] = useState<LedgerDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await feedbackLearningApi.listLessons({
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
        profile_id: profileFilter.trim() || undefined,
        limit: PAGE_LIMIT,
      });
      setRows(data);
    } catch (e) {
      setError(errorMessage(e, 'failed to load the learning ledger'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sourceFilter, profileFilter]);

  useEffect(() => {
    if (selected) return;
    void load();
  }, [load, selected]);

  const open = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      setSelected(await feedbackLearningApi.getLesson(id));
    } catch (e) {
      setError(errorMessage(e, 'failed to load the lesson'));
    } finally {
      setLoading(false);
    }
  }, []);

  const visible = useMemo(
    () => rows.filter((r) => matchesKbWriteFilter(r, kbWriteFilter)),
    [rows, kbWriteFilter],
  );

  if (selected) {
    return <LessonDetailView detail={selected} onBack={() => setSelected(null)} />;
  }

  return (
    <div data-section="feedback_lessons">
      <div className={styles.filters}>
        <label htmlFor="lesson-status">Status</label>
        <select
          id="lesson-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | LearningSignalStatus)}
          data-action="agent_eval_lessons_status_filter"
          data-record-value="status"
        >
          <option value="">Any</option>
          {LEARNING_SIGNAL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label htmlFor="lesson-source">Source</label>
        <select
          id="lesson-source"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as '' | LearningSignalSource)}
          data-action="agent_eval_lessons_source_filter"
          data-record-value="source"
        >
          <option value="">Any</option>
          {LEARNING_SIGNAL_SOURCES.map((s) => (
            <option key={s} value={s}>
              {SOURCE_DISPLAY[s]}
            </option>
          ))}
        </select>
        <label htmlFor="lesson-profile">Profile</label>
        <input
          id="lesson-profile"
          placeholder="(any)"
          value={profileFilter}
          onChange={(e) => setProfileFilter(e.target.value)}
          data-action="agent_eval_lessons_profile_filter"
          data-record-value="profile_id"
        />
        <label htmlFor="lesson-kb-write">KB write</label>
        <select
          id="lesson-kb-write"
          value={kbWriteFilter}
          onChange={(e) => setKbWriteFilter(e.target.value as KbWriteFilter)}
          data-action="agent_eval_lessons_kb_write_filter"
          data-record-value="kb_write"
        >
          <option value="any">Any</option>
          <option value="written">Wrote a lesson</option>
          <option value="none">No write</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <table className={styles.list} aria-label="Learning ledger">
        <thead>
          <tr>
            <th>Created</th>
            <th>Source</th>
            <th>Profile</th>
            <th>Status</th>
            <th>KB writes</th>
            <th>Questions</th>
            <th>Eval case</th>
            <th>Cost</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => (
            <tr
              key={r.id}
              className={styles.clickable}
              onClick={() => void open(r.id)}
              data-action="agent_eval_lesson_open"
              data-target-lesson-id={r.id}
              data-outcome="open"
            >
              <td>{formatTs(r.created_at)}</td>
              <td>{SOURCE_DISPLAY[r.source] ?? r.source}</td>
              <td>{r.profile_id ?? '—'}</td>
              <td>
                <span className={statusTagClass(r.status)}>{r.status}</span>
                {r.attempts > 1 && <span className={styles.sub}> ×{r.attempts}</span>}
              </td>
              <td>{r.kb_writes?.length ?? 0}</td>
              <td>{r.questions?.length ?? 0}</td>
              <td>{r.eval_case_id ? 'proposed' : '—'}</td>
              <td>{formatUsd(r.cost_usd)}</td>
              <td className={styles['cell-error']} title={r.error ?? undefined}>
                {r.error ?? '—'}
              </td>
            </tr>
          ))}
          {!loading && visible.length === 0 && (
            <tr>
              <td colSpan={9} className={styles.empty}>
                {rows.length === 0
                  ? 'no ledger rows match these filters'
                  : 'no rows on this page match the KB-write filter'}
              </td>
            </tr>
          )}
          {loading && (
            <tr>
              <td colSpan={9} className={styles.empty}>
                loading…
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rows.length === PAGE_LIMIT && (
        <div className={styles.sub} style={{ marginTop: 8 }}>
          Showing the newest {PAGE_LIMIT} rows; narrow the filters to see older ones.
        </div>
      )}
    </div>
  );
}
